/**
 * API utility functions for IWMS module
 */

import { API_URL, getAuthHeaders } from './config';

// Re-export for backwards compatibility
export { API_URL, getAuthHeaders };

// Work Orders API
export const workOrdersApi = {
  list: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/api/work-orders${queryString ? `?${queryString}` : ''}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch work orders');
    return response.json();
  },

  get: async (id) => {
    const response = await fetch(`${API_URL}/api/work-orders/${id}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch work order');
    return response.json();
  },

  create: async (data) => {
    const response = await fetch(`${API_URL}/api/work-orders`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create work order');
    return response.json();
  },

  update: async (id, data) => {
    const response = await fetch(`${API_URL}/api/work-orders/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update work order');
    return response.json();
  },

  complete: async (id, data = {}) => {
    // v1.2.7 — Switched from query-string to JSON body so completion
    // photos (base64) can be attached. Backend remains backwards
    // compatible with the old query-string call shape.
    const response = await fetch(`${API_URL}/api/work-orders/${id}/complete`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      // Surface the server-side detail so UI toasts are diagnostic
      let detail = '';
      try { const j = await response.json(); detail = j.detail ? (typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail)) : ''; } catch { /* noop */ }
      throw new Error(detail || `HTTP ${response.status}`);
    }
    return response.json();
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/api/work-orders/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete work order');
    return response.json();
  },

  getStats: async (clientId = null) => {
    const params = clientId ? `?client_id=${clientId}` : '';
    const response = await fetch(`${API_URL}/api/work-orders/stats/summary${params}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch work order stats');
    return response.json();
  },

  getUnreadMessagesCount: async () => {
    const response = await fetch(`${API_URL}/api/work-orders/unread-count`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch unread messages count');
    return response.json();
  },

  getActiveCount: async () => {
    const response = await fetch(`${API_URL}/api/work-orders/active-count`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch active work orders count');
    return response.json();
  },

  getConversations: async (limit = 50) => {
    const response = await fetch(`${API_URL}/api/work-orders/conversations?limit=${limit}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch conversations');
    return response.json();
  },

  getMessages: async (workOrderId) => {
    const response = await fetch(`${API_URL}/api/work-orders/${workOrderId}/messages`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch messages');
    return response.json();
  },

  sendMessage: async (workOrderId, message) => {
    const response = await fetch(`${API_URL}/api/work-orders/${workOrderId}/messages`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ message })
    });
    if (!response.ok) throw new Error('Failed to send message');
    return response.json();
  },

  markMessagesRead: async (workOrderId) => {
    const response = await fetch(`${API_URL}/api/work-orders/${workOrderId}/messages/read`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to mark messages as read');
    return response.json();
  }
};

// PM Schedules API
export const pmSchedulesApi = {
  list: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/api/pm-schedules${queryString ? `?${queryString}` : ''}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch PM schedules');
    return response.json();
  },

  get: async (id) => {
    const response = await fetch(`${API_URL}/api/pm-schedules/${id}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch PM schedule');
    return response.json();
  },

  create: async (data) => {
    const response = await fetch(`${API_URL}/api/pm-schedules`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create PM schedule');
    return response.json();
  },

  update: async (id, data) => {
    const response = await fetch(`${API_URL}/api/pm-schedules/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update PM schedule');
    return response.json();
  },

  generateWorkOrder: async (id) => {
    const response = await fetch(`${API_URL}/api/pm-schedules/${id}/generate-work-order`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to generate work order');
    return response.json();
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/api/pm-schedules/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete PM schedule');
    return response.json();
  },

  getStats: async (clientId = null) => {
    const params = clientId ? `?client_id=${clientId}` : '';
    const response = await fetch(`${API_URL}/api/pm-schedules/stats/summary${params}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch PM stats');
    return response.json();
  }
};

// Warranties API
export const warrantiesApi = {
  list: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/api/warranties${queryString ? `?${queryString}` : ''}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch warranties');
    return response.json();
  },

  get: async (id) => {
    const response = await fetch(`${API_URL}/api/warranties/${id}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch warranty');
    return response.json();
  },

  create: async (data) => {
    const response = await fetch(`${API_URL}/api/warranties`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create warranty');
    return response.json();
  },

  update: async (id, data) => {
    const response = await fetch(`${API_URL}/api/warranties/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update warranty');
    return response.json();
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/api/warranties/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete warranty');
    return response.json();
  },

  getStats: async (clientId = null) => {
    const params = clientId ? `?client_id=${clientId}` : '';
    const response = await fetch(`${API_URL}/api/warranties/stats/summary${params}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch warranty stats');
    return response.json();
  }
};

// Vendors API
export const vendorsApi = {
  list: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/api/vendors${queryString ? `?${queryString}` : ''}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch vendors');
    return response.json();
  },

  get: async (id) => {
    const response = await fetch(`${API_URL}/api/vendors/${id}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch vendor');
    return response.json();
  },

  create: async (data) => {
    const response = await fetch(`${API_URL}/api/vendors`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create vendor');
    return response.json();
  },

  update: async (id, data) => {
    const response = await fetch(`${API_URL}/api/vendors/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update vendor');
    return response.json();
  },

  assignToClient: async (vendorId, clientId) => {
    const response = await fetch(`${API_URL}/api/vendors/${vendorId}/assign-client/${clientId}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to assign vendor to client');
    return response.json();
  },

  unassignFromClient: async (vendorId, clientId) => {
    const response = await fetch(`${API_URL}/api/vendors/${vendorId}/assign-client/${clientId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to unassign vendor from client');
    return response.json();
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/api/vendors/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete vendor');
    return response.json();
  },

  getSpecialties: async () => {
    const response = await fetch(`${API_URL}/api/vendors/specialties/list`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch specialties');
    return response.json();
  }
};

// Leases API
export const leasesApi = {
  list: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/api/leases${queryString ? `?${queryString}` : ''}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch leases');
    return response.json();
  },

  get: async (id) => {
    const response = await fetch(`${API_URL}/api/leases/${id}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch lease');
    return response.json();
  },

  create: async (data) => {
    const response = await fetch(`${API_URL}/api/leases`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create lease');
    return response.json();
  },

  update: async (id, data) => {
    const response = await fetch(`${API_URL}/api/leases/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update lease');
    return response.json();
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/api/leases/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete lease');
    return response.json();
  },

  getFacilityResponsibilities: async (facilityId) => {
    const response = await fetch(`${API_URL}/api/leases/facility/${facilityId}/responsibilities`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch facility responsibilities');
    return response.json();
  }
};

// Features API
export const featuresApi = {
  getMyFeatures: async () => {
    const response = await fetch(`${API_URL}/api/features/my-features`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch features');
    return response.json();
  },

  getClientFeatures: async (clientId) => {
    const response = await fetch(`${API_URL}/api/features/clients/${clientId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch client features');
    return response.json();
  },

  updateClientFeatures: async (clientId, features) => {
    const response = await fetch(`${API_URL}/api/features/clients/${clientId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(features)
    });
    if (!response.ok) throw new Error('Failed to update client features');
    return response.json();
  },

  updateClientSLA: async (clientId, slaSettings) => {
    const response = await fetch(`${API_URL}/api/features/clients/${clientId}/sla`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(slaSettings)
    });
    if (!response.ok) throw new Error('Failed to update SLA settings');
    return response.json();
  },

  updateClientCode: async (clientId, clientCode) => {
    const response = await fetch(`${API_URL}/api/features/clients/${clientId}/code?client_code=${clientCode}`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to update client code');
    return response.json();
  }
};
